import React from "react";
import { useState, useEffect } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import WelcomeScreen from "./components/WelcomeScreen";
import ExtendedWarrantyWelcomeScreen from "./components/extended-warranty/ExtendedWarrantyWelcomeScreen";
import QualificationScreen from "./components/extended-warranty/QualificationScreen";
import ExtendedWarrantySurveyScreen from "./components/extended-warranty/ExtendedWarrantySurveyScreen";
import ExtendedWarrantyExitScreen from "./components/extended-warranty/ExtendedWarrantyExitScreen";
import ExtendedWarrantyChoiceScreen from "./components/extended-warranty/ExtendedWarrantyChoiceScreen";
import ExtendedWarrantyPolicyUploadScreen from "./components/extended-warranty/ExtendedWarrantyPolicyUploadScreen";
import ExtendedWarrantyVehicleSelection from "./components/extended-warranty/ExtendedWarrantyVehicleSelection";
import ExtendedWarrantyVehicleCompletion from "./components/extended-warranty/ExtendedWarrantyVehicleCompletion";
import ExtendedWarrantyOfferScreen from "./components/extended-warranty/ExtendedWarrantyOfferScreen";
import ExtendedWarrantyHandoff from "./components/extended-warranty/ExtendedWarrantyHandoff";
import MileageCheckinScreen from "./components/MileageCheckinScreen";
import Survey from "./components/SurveyScreen";
import Verification from "./components/VerificationScreen";
import DocumentUpload from "./components/DocumentUploadScreen";
import Success from "./components/SuccessScreen";
import CanopyConnectScreen from "./components/CanopyConnectScreen";
import InsurancePolicyUploader from "./components/InsurancePolicyUploader";
import ProgressIndicator from "./components/ProgressIndicator";
import Navbar from "./components/Navbar";
import { useAuth } from "./hooks/useAuth";
import { useFlow, useFlowSteps } from "./hooks/useFlow";
import PostAuthenticationSurvey from "./components/PostAuthenticationSurvey";
import UserService from "./lib/UserService";
import DimoConnectScreen from "./components/DimoConnectScreen";
import ContactInfoScreen from "./components/ContactInfoScreen";
import ReviewSubmitScreen from "./components/ReviewSubmitScreen";
import usePageTracking from "./usePageTracking"; // your tracking hook
import { useAuthTracking, useErrorTracking } from "./hooks/useAnalytics";
import analyticsService from "./lib/AnalyticsService";
import { useReferrer } from "./hooks/useReferrer";
import ReferrerManager from "./utils/ReferrerManager";
import RenewalWelcomeScreen from "./components/renewal/RenewalWelcomeScreen";
import RenewalPolicyUploader from "./components/renewal/RenewalPolicyUploader";

// Configuration flag for DIMO integration
const referral = ReferrerManager.getReferrerWithSubdomainFallback();
const SYSTEM_CONFIG = {
  ENABLE_DIMO: referral === "dimo", // Set to true to enable DIMO screen, false to skip it
};

const App = () => {
  const { step } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get flow configuration
  const {
    flowType,
    isExtendedWarranty,
    isInsuranceRenewal,
    shouldShowProgress,
    config,
  } = useFlow();
  const STEPS = useFlowSteps();
  const [userData, setUserData] = useState(() => {
    const savedData = sessionStorage.getItem("userData");
    return savedData ? JSON.parse(savedData) : null;
  });
  const { user, getIdToken, loading: authLoading, logout } = useAuth(); // Get the authenticated user and loading state
  const [surveyData, setSurveyData] = useState(() => {
    const savedData = sessionStorage.getItem("surveyData");
    return savedData ? JSON.parse(savedData) : {};
  });

  const [isLoading, setIsLoading] = useState(true);
  const [serverUserLoading, setServerUserLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [initComplete, setInitComplete] = useState(false);
  const [currentStepComponent, setCurrentStepComponent] = useState(null);

  // Analytics hooks
  const { trackLogout } = useAuthTracking();
  const { trackError } = useErrorTracking();

  usePageTracking();
  const { isLoading: referrerLoading, displayConfig } = useReferrer();

  // Use an effect to update the component when step changes
  useEffect(() => {
    // Create the component based on the current step and flow
    let component;
    // Handle welcome screen based on flow type
    if (step === STEPS.WELCOME) {
      if (isExtendedWarranty()) {
        component = (
          <ExtendedWarrantyWelcomeScreen
            onComplete={() => handleStepComplete(STEPS.WELCOME)}
          />
        );
      } else if (isInsuranceRenewal()) {
        component = (
          <RenewalWelcomeScreen
            onComplete={() => handleStepComplete(STEPS.WELCOME)}
          />
        );
      } else {
        component = (
          <WelcomeScreen
            isReturningUser={isReturningUser}
            userData={userData}
            onComplete={() => handleStepComplete(STEPS.WELCOME)}
            onFullApp={handleFullAppTransition}
            onReset={handleReset}
            partnerName={
              referrerLoading ? undefined : displayConfig.partnerName
            }
          />
        );
      }
    }
    // Handle other steps
    else {
      switch (step) {
        case STEPS.QUALIFICATION:
          component = (
            <QualificationScreen
              onComplete={(data) => {
                handleStepComplete(STEPS.QUALIFICATION, data);
              }}
              onExit={(data) => handleQualificationExit(data)}
            />
          );
          break;
        case STEPS.SURVEY:
          component = isExtendedWarranty() ? (
            <ExtendedWarrantySurveyScreen
              onComplete={(data) => handleStepComplete(STEPS.SURVEY, data)}
              onBack={() => navigateToStep(STEPS.VERIFICATION)}
            />
          ) : (
            <Survey
              isReturningUser={isReturningUser}
              onComplete={(data) => handleStepComplete(STEPS.SURVEY, data)}
              onFullApp={handleFullAppTransition}
              onReset={handleReset}
              onBack={() => navigateToStep(STEPS.DIMO)}
            />
          );
          break;
        case "vehicle-selection": // Standalone route for testing
          component = (
            <ExtendedWarrantyVehicleSelection
              onComplete={(vehicleData) => {
                // Navigate directly to completion page
                navigate("/extended-warranty/vehicle-completion", {
                  replace: false,
                });
              }}
              onNoVehicles={() => {
                navigate("/extended-warranty/extended-warranty-policy", {
                  replace: false,
                });
              }}
            />
          );
          break;
        case "vehicle-completion": // Standalone route for testing
          component = (
            <ExtendedWarrantyVehicleCompletion
              selectedVehicle={JSON.parse(
                sessionStorage.getItem("selectedVehicle") || "{}"
              )}
              onComplete={(completeVehicleData) => {
                // Navigate directly to handoff page
                //onComplete={(data) => handleStepComplete(STEPS.SURVEY, data)}
                handleStepComplete(STEPS.VEHICLE_COMPLETION);
                //navigate("/extended-warranty/extended-warranty-offer", { replace: false });
              }}
              onBack={() => {
                navigate("/extended-warranty/vehicle-selection", {
                  replace: false,
                });
              }}
            />
          );
          break;
        case STEPS.EXTENDED_WARRANTY_OFFER:
          component = (
            <ExtendedWarrantyOfferScreen
              warrantyProvider={"Chaiz"} // You'll need to implement this
              onAcceptOffer={(provider) =>
                handleStepComplete(STEPS.EXTENDED_WARRANTY_OFFER, {
                  accepted: true,
                  provider,
                })
              }
              onReject={() =>
                handleStepComplete(STEPS.EXTENDED_WARRANTY_OFFER, {
                  accepted: false,
                })
              }
            />
          );
          break;
        case STEPS.EXTENDED_WARRANTY_HANDOFF:
          component = (
            <ExtendedWarrantyHandoff
              vehicleData={JSON.parse(
                sessionStorage.getItem("completeVehicleData") || "{}"
              )}
              onComplete={() => {
                console.log("Handoff completed");
              }}
            />
          );
          break;
        case STEPS.EXTENDED_WARRANTY_CHOICE:
          component = (
            <ExtendedWarrantyChoiceScreen
              onPolicyUpload={() =>
                handleStepComplete(STEPS.EXTENDED_WARRANTY_CHOICE, {
                  choice: "policy",
                })
              }
              onMileageCheckin={() =>
                handleStepComplete(STEPS.EXTENDED_WARRANTY_CHOICE, {
                  choice: "mileage",
                })
              }
              onComplete={() =>
                handleStepComplete(STEPS.EXTENDED_WARRANTY_CHOICE)
              } // Just calls handleStepComplete
              onBack={() => navigateToStep(STEPS.SURVEY)}
            />
          );
          break;
        case STEPS.EXTENDED_WARRANTY_POLICY:
          component = (
            <ExtendedWarrantyPolicyUploadScreen
              onComplete={(data) =>
                handleStepComplete(STEPS.EXTENDED_WARRANTY_POLICY, data)
              }
              onBack={() => navigateToStep(STEPS.VEHICLE_SELECTION)}
            />
          );
          break;
        case STEPS.EXTENDED_WARRANTY_MILEAGE:
          component = (
            <MileageCheckinScreen
              onComplete={(data) =>
                handleStepComplete(STEPS.EXTENDED_WARRANTY_MILEAGE, data)
              }
              onBack={() => navigateToStep(STEPS.EXTENDED_WARRANTY_CHOICE)}
            />
          );
          break;
        case STEPS.EXIT:
          component = (
            <ExtendedWarrantyExitScreen
              onExit={() => (window.location.href = "https://www.ownli.co")}
            />
          );
          break;
        case STEPS.VERIFICATION:
          component = (
            <Verification
              onComplete={(data) =>
                handleStepComplete(STEPS.VERIFICATION, data)
              }
              onFullApp={handleFullAppTransition}
            />
          );
          break;
        case STEPS.DIMO:
          // If DIMO is disabled but somehow we got to this route, redirect to POST_AUTH_SURVEY
          if (!SYSTEM_CONFIG.ENABLE_DIMO) {
            component = <Navigate to={`/onboarding/${STEPS.SURVEY}`} replace />;
          } else {
            component = (
              <DimoConnectScreen
                userData={userData}
                onComplete={(data) => handleStepComplete(STEPS.DIMO, data)}
                onSkip={() => handleStepComplete(STEPS.DIMO)}
                onFullApp={handleFullAppTransition}
              />
            );
          }
          break;
        case STEPS.CONTACT_INFO:
          component = (
            <ContactInfoScreen
              userData={userData}
              onComplete={(data) =>
                handleStepComplete(STEPS.CONTACT_INFO, data)
              }
              onBack={() => (isInsuranceRenewal()? navigateToStep(STEPS.RENEWAL_SURVEY): navigateToStep(STEPS.SURVEY))}
            />
          );
          break;
        case STEPS.POST_AUTH_SURVEY:
          component = (
            <PostAuthenticationSurvey
              onComplete={(data) =>
                handleStepComplete(STEPS.POST_AUTH_SURVEY, data)
              }
              onBack={() => navigateToStep(STEPS.CONTACT_INFO)}
            />
          );
          break;
        case STEPS.RENEWAL_SURVEY:
          component = (
            <PostAuthenticationSurvey
              onComplete={(data) =>
                handleStepComplete(STEPS.RENEWAL_SURVEY, data)
              }
              onBack={() => navigateToStep(STEPS.POLICIES)}
              renewal={true}
            />
          );
          break;
        case STEPS.INSURANCE_OPTIONS:
          component = (
            <InsurancePolicyUploader
              onUpload={handleManualUpload}
              onSync={handleSyncOption}
              onComplete={() => handleStepComplete(STEPS.INSURANCE_OPTIONS)}
              onBack={() => navigateToStep(STEPS.POST_AUTH_SURVEY)}
            />
          );
          break;
        case STEPS.POLICIES:
          component = (
            <RenewalPolicyUploader
              onComplete={() => handleStepComplete(STEPS.POLICIES)}
            />
          );
          break;
        case STEPS.DOCUMENT:
          component = (
            <DocumentUpload
              userData={userData}
              onComplete={() => handleStepComplete(STEPS.DOCUMENT)}
              onBack={handleBackToOptions}
              onFullApp={handleFullAppTransition}
              reupload={false}
            />
          );
          break;
        case STEPS.DOCUMENT_REUPLOAD:
          component = (
            <DocumentUpload
              userData={userData}
              onComplete={() => handleStepComplete(STEPS.DOCUMENT)}
              onBack={handleBackToOptions}
              onFullApp={handleFullAppTransition}
              reupload={true}
            />
          );
          break;
        case STEPS.CANOPY:
          component = (
            <CanopyConnectScreen
              userData={userData}
              onComplete={() => handleStepComplete(STEPS.CANOPY)}
              onBack={handleBackToOptions}
              onFullApp={handleFullAppTransition}
            />
          );
          break;
        case STEPS.REVIEW_SUBMIT: // New case
          component = (
            <ReviewSubmitScreen
              userData={userData}
              onComplete={() => handleStepComplete(STEPS.REVIEW_SUBMIT)}
              onBack={() => navigateToStep(STEPS.INSURANCE_OPTIONS)} // Navigate back to either DOCUMENT or CANOPY
            />
          );
          break;
        case STEPS.SUCCESS:
          component = (
            <Success
              userData={userData}
              onComplete={(data) => handleStepComplete(STEPS.SUCCESS, data)}
              onFullApp={handleFullAppTransition}
              renewal={false}
            />
          );
          break;
        case STEPS.SUCCESS_RENEWAL:
          component = (
            <Success
              renewal={true}
              userData={userData}
              onComplete={(data) => handleStepComplete(STEPS.SUCCESS, data)}
              onFullApp={handleFullAppTransition}
            />
          );
          break;
        default:
          component = <Navigate to={config.defaultRoute} replace />;
      }
    }
    // Update state with the new component
    setCurrentStepComponent(component);
  }, [step, flowType, isExtendedWarranty]);

  // Effect for URL params initialization
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const encryptedData = urlParams.get("data");
        const timestamp = urlParams.get("timestamp");
        const partner = urlParams.get("partner");

        if (!encryptedData || !timestamp || !partner) {
          console.warn("Missing some required URL parameters");
          // Don't throw error here, just log a warning
        }

        setInitComplete(true);
      } catch (err) {
        console.error("Error in initialization:", err);
        setError(err.message);
        setInitComplete(true);
      }
    };

    initializeApp();
  }, []);

  useEffect(() => {
    let userCheckInProgress = false;

    const checkUserOnServer = async () => {
      if (!user) {
        return; // No user, no need to check
      }

      if (userCheckInProgress) {
        return; // Prevent duplicate calls
      }

      userCheckInProgress = true;

      try {
        setServerUserLoading(true);
        // Get Firebase token
        const token = await getIdToken();

        // First, just check if the user exists using the /me endpoint
        const serverUserData = await UserService.getUserIfExists(token);

        if (serverUserData) {
          // User exists, store the data
          setUserData(serverUserData);
          sessionStorage.setItem("userData", JSON.stringify(serverUserData));

          // Identify user in analytics
          analyticsService.identifyUser(user.uid, {
            name: user.displayName,
            email: user.email,
            phone: user.phoneNumber,
            server_user_id: serverUserData.id,
            is_returning_user: true,
            createdAt: user.metadata?.creationTime,
          });

          if (serverUserData !== null) {
            setIsReturningUser(true);
          }
        } else {
          // User doesn't exist but we're authenticated
          // Don't create yet - we'll do that after explicit authentication
          console.log(
            "User authenticated but not found on server - awaiting verification step"
          );
        }
      } catch (error) {
        console.error("Failed to check server user:", error);
        trackError("server_user_check_error", error, {
          has_user: !!user,
          user_id: user?.uid,
        });
      } finally {
        setServerUserLoading(false);
        userCheckInProgress = false;
      }
    };

    // Only run this if auth loading is complete
    if (!authLoading) {
      checkUserOnServer();
    }

    return () => {
      userCheckInProgress = false;
    };
  }, [user, authLoading, getIdToken]);

  // Combined effect for overall loading state
  useEffect(() => {
    if (!initComplete) {
      // Still initializing from URL params
      return;
    }

    if (authLoading) {
      // Still waiting for Firebase auth
      setIsLoading(true);
      return;
    }

    if (referrerLoading) {
      // Still waiting for referrer/databuyer data
      setIsLoading(true);
      return;
    }

    if (user && serverUserLoading) {
      // Waiting for server user data
      setIsLoading(true);
      return;
    }

    // All loading complete
    setIsLoading(false);
  }, [initComplete, authLoading, referrerLoading, user, serverUserLoading]);

  // Effect for returning user logic
  useEffect(() => {
    if (isLoading) return;

    const hasServerUserRecord = userData && userData.id;
    // If user is authenticated and on welcome page, prepare to redirect
    if (hasServerUserRecord && step === STEPS.WELCOME) {
      setIsReturningUser(true);
      // Don't redirect immediately to give welcome screen a chance to display
      // The welcome screen will handle continue action through onComplete
    }
    // For users arriving directly at verification, skip if already authenticated
    if (hasServerUserRecord && step === STEPS.VERIFICATION) {
      // Redirect to post auth survey since user is already verified
      console.log("User already verified with server, skipping verification");
      if (isExtendedWarranty()) {
        console.log("next step should be " + STEPS.SURVEY);
        navigateToStep(STEPS.SURVEY);
      } else if (isInsuranceRenewal()) {
        navigateToStep(STEPS.INSURANCE_OPTIONS);
      } else {
        navigateToStep(STEPS.DIMO);
      }
    }
  }, [user, userData, step, isLoading]);

  // Validate the current step from URL
  useEffect(() => {
    if (isLoading) return;

    const validSteps = Object.values(STEPS);
    if (!validSteps.includes(step)) {
      navigate("/onboarding/welcome", { replace: true });
    }
  }, [step, navigate, isLoading]);

  const navigateToStep = (nextStep, data = null) => {
    const currentStep = step;

    // Preserve any existing query parameters
    const currentParams = new URLSearchParams(window.location.search);
    const currentQuery = window.location.search;
    // Use flow-aware routing
    const currentUrl = `${config.baseRoute}/${currentStep}${currentQuery}`;
    const targetUrl = `${config.baseRoute}/${nextStep}${currentQuery}`;
    console.log(currentUrl);
    console.log(targetUrl);

    if (currentUrl === targetUrl) {
      console.log("Skipping redundant navigation to", targetUrl);
      return;
    }
    // Preserve important URL parameters
    const preserveParams = [
      "partner",
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_referrer",
      "data",
      "timestamp",
    ];

    const queryParams = new URLSearchParams();
    preserveParams.forEach((param) => {
      if (currentParams.has(param)) {
        queryParams.set(param, currentParams.get(param));
      }
    });

    // Build the query string
    const queryString = queryParams.toString();
    const queryStringFormatted = queryString ? `?${queryString}` : "";

    const replaceSteps = new Set(["welcome", "dimo"]);
    const replace = replaceSteps.has(nextStep);

    console.log(`${config.baseRoute}/${nextStep}${queryStringFormatted}`);
    // Navigate to the next step using flow-aware routing
    navigate(`${config.baseRoute}/${nextStep}${queryStringFormatted}`, {
      replace: replace,
      // Pass state data if needed
      state: {
        ...(location.state || {}),
        ...(data ? { [nextStep + "Data"]: data } : {}),
      },
    });
  };

  const handleStepComplete = async (step, data) => {
    // Track step completion
    const eventName = isExtendedWarranty()
      ? "Extended Warranty Step Complete"
      : isInsuranceRenewal()
      ? "Insurance Renewal Step Complete"
      : "Insurance Profile Step Complete";

    analyticsService.trackEvent(eventName, {
      step_name: step,
      flow_type: flowType,
      has_data: !!data,
      data_keys: data ? Object.keys(data) : [],
      is_returning_user: isReturningUser,
      user_id: user?.uid,
    });

    let nextStep;

    // Use flow-specific logic for step progression
    if (isExtendedWarranty()) {
      nextStep = getExtendedWarrantyNextStep(step, data);
    } else if (isInsuranceRenewal()) {
      nextStep = getInsuranceRenewalNextStep(step, data);
    } else {
      nextStep = getInsuranceProfileNextStep(step, data);
    }
    if (nextStep) {
      navigateToStep(nextStep, data);
    }
  };

  // Extended Warranty flow step logic
  const getExtendedWarrantyNextStep = (step, data) => {
    switch (step) {
      case STEPS.WELCOME:
        return STEPS.QUALIFICATION;
      case STEPS.QUALIFICATION:
        console.log("Qualification " + data.qualifies);
        console.log(STEPS.VERIFICATION);
        return data?.qualifies ? STEPS.VERIFICATION : STEPS.EXIT;
      case STEPS.VERIFICATION:
        return STEPS.SURVEY;
      case STEPS.SURVEY:
        console.log("well return " + STEPS.CONTACT_INFO);
        return STEPS.CONTACT_INFO;
      case STEPS.CONTACT_INFO:
        return STEPS.VEHICLE_SELECTION;
      case STEPS.GET_CANDIDATES:
        // This step would check if user has eligible vehicles
        if (data?.hasCandidates) {
          return STEPS.VEHICLE_SELECTION;
        } else {
          return STEPS.EXTENDED_WARRANTY_CHOICE;
        }
      case STEPS.VEHICLE_SELECTION:
        return STEPS.VEHICLE_COMPLETION;
      case STEPS.VEHICLE_COMPLETION:
        console.log("vehicle completion");
        return STEPS.EXTENDED_WARRANTY_OFFER;
      case STEPS.EXTENDED_WARRANTY_CHOICE:
        console.log("WE want a next step");
        console.log(data);
        // Route based on user's choice
        if (data?.choice === "policy") {
          return STEPS.EXTENDED_WARRANTY_POLICY;
        } else if (data?.choice === "mileage") {
          return STEPS.EXTENDED_WARRANTY_MILEAGE;
        }
        return STEPS.VEHICLE_COMPLETION;
      case STEPS.EXTENDED_WARRANTY_POLICY:
      case STEPS.EXTENDED_WARRANTY_MILEAGE:
        // After completing either verification method, go to success
        return STEPS.VEHICLE_SELECTION;
      case STEPS.EXTENDED_WARRANTY_OFFER:
        console.log("vehicle completion");
        return STEPS.EXTENDED_WARRANTY_HANDOFF;
      case STEPS.EXIT:
        return null; // Terminal state
      default:
        return null;
    }
  };

  const getInsuranceRenewalNextStep = (step, data) => {
    switch (step) {
      case STEPS.WELCOME:
        console.log("isReturningUser", isReturningUser);
        console.log("user", user);
        // For returning users, skip to insurance options
        if (isReturningUser && user) {
          return STEPS.POLICIES;
        } else {
          return STEPS.VERIFICATION;
        }
      case STEPS.VERIFICATION:
        return STEPS.POLICIES;
      case STEPS.POLICIES:
        return STEPS.RENEWAL_SURVEY;
      case STEPS.RENEWAL_SURVEY:
        console.log("well return " + STEPS.CONTACT_INFO);
        return STEPS.CONTACT_INFO;
      case STEPS.CONTACT_INFO:
        return STEPS.SUCCESS_RENEWAL;
      default:
        return null;
    }
  };

  // Insurance Profile flow step logic (existing)
  const getInsuranceProfileNextStep = (step, data) => {
    switch (step) {
      case STEPS.WELCOME:
        console.log("isReturningUser", isReturningUser);
        console.log("user", user);
        // For returning users, skip to insurance options
        if (isReturningUser && user) {
          return SYSTEM_CONFIG.ENABLE_DIMO ? STEPS.DIMO : STEPS.SURVEY;
        } else {
          return STEPS.VERIFICATION;
        }
      case STEPS.VERIFICATION:
        return SYSTEM_CONFIG.ENABLE_DIMO ? STEPS.DIMO : STEPS.SURVEY;
      case STEPS.DIMO:
        return STEPS.SURVEY; // After DIMO, go to insurance options
      case STEPS.SURVEY:
        setSurveyData(data);
        sessionStorage.setItem("surveyData", JSON.stringify(data));
        return STEPS.CONTACT_INFO;
      case STEPS.CONTACT_INFO:
        return STEPS.POST_AUTH_SURVEY; // After DIMO, go to insurance options
      case STEPS.POST_AUTH_SURVEY: // Add this new case
        // Store post auth survey data if needed
        if (data) {
          sessionStorage.setItem(
            "postAuthSurveyCompleteData",
            JSON.stringify(data)
          );
        }
        return STEPS.INSURANCE_OPTIONS;
      case STEPS.INSURANCE_OPTIONS:
        // The navigation from this step will be handled by the component
        // via handleManualUpload or handleSyncOption
        return STEPS.SUCCESS; // Document upload now goes directly to success
      case STEPS.DOCUMENT:
        return STEPS.INSURANCE_OPTIONS;
      case STEPS.DOCUMENT_REUPLOAD: // Document upload now goes directly to success
        return STEPS.INSURANCE_OPTIONS;
      case STEPS.CANOPY:
        return STEPS.SUCCESS; // Canopy now goes directly to success
      case STEPS.REVIEW_SUBMIT: // New case
        return STEPS.SUCCESS;
      case STEPS.SUCCESS:
        return STEPS.SUCCESS;
      default:
        console.error("Unknown step:", step);
        return null;
    }
  };

  // Handle qualification exit (when user doesn't qualify)
  const handleQualificationExit = (data) => {
    // Track the exit
    analyticsService.trackEvent("Extended Warranty Qualification Exit", {
      reason: data?.reason || "unknown",
      car_age: data?.carAge,
      user_id: user?.uid,
    });

    // You could redirect to a "sorry" page or back to main site
    // For now, let's redirect to a generic exit
    window.location.href = "https://www.ownli.co";
  };

  // Handlers for insurance options screen
  const handleManualUpload = (policy) => {
    console.log("HANDLE MANUAL UPLOAD");
    if (policy) {
      navigateToStep(STEPS.DOCUMENT_REUPLOAD);
    } else {
      navigateToStep(STEPS.DOCUMENT);
    }
  };

  const handleSyncOption = () => {
    navigateToStep(STEPS.CANOPY);
  };

  const handleReset = async () => {
    // Track reset action
    analyticsService.trackEvent("Insurance Profile App Reset", {
      step_at_reset: step,
      user_id: user?.uid,
      had_user_data: !!userData,
    });

    // Sign out the Firebase user if logged in
    if (user) {
      try {
        await logout();
        trackLogout("reset");
        console.log("Firebase user signed out");
      } catch (error) {
        console.error("Error signing out Firebase user:", error);
        trackError("logout_error", error, { context: "app_reset" });
      }
    }
    // Clear all session data
    sessionStorage.removeItem("authUser");
    sessionStorage.removeItem("dimoData");
    sessionStorage.removeItem("profileSubmitted");
    sessionStorage.removeItem("surveySubmitted");
    sessionStorage.removeItem("postAuthSurveyCompleteData");
    sessionStorage.removeItem("postAuthSurveyQuestionIndex");
    sessionStorage.removeItem("postAuthSurveyStep");
    sessionStorage.removeItem("postAuthSurveyData");
    sessionStorage.removeItem("userData");
    sessionStorage.removeItem("contactInfoData");
    sessionStorage.removeItem("surveyData");
    sessionStorage.removeItem("surveyCurrentQuestionIndex");
    sessionStorage.removeItem("verificationData");
    sessionStorage.removeItem("documentData");
    sessionStorage.removeItem("surveyShowThankYou");
    sessionStorage.removeItem("contactPreferencesData");
    sessionStorage.removeItem("userLoginType");

    // Navigate back to welcome page
    navigate("/onboarding/welcome", { replace: true });

    // Reload to ensure clean state
    window.location.reload();
  };

  const handleFullAppTransition = () => {
    // Generate secure token containing user data and survey responses
    const tokenData = {
      userData,
      surveyData,
      timestamp: Date.now(),
    };

    // In production, this would be properly encrypted
    const token = btoa(JSON.stringify(tokenData));

    // Redirect to full app experience
    window.open(`https://www.ownli.app?token=${token}`, "_blank");
  };

  const handleBackToOptions = () => {
    navigateToStep(STEPS.INSURANCE_OPTIONS);
  };

  const LoadingOverlay = () => (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white p-4 rounded-lg shadow-lg flex items-center gap-3">
        <div className="w-5 h-5 border-2 border-t-transparent border-blue-600 rounded-full animate-spin"></div>
        <div className="text-gray-800">Loading your information...</div>
      </div>
    </div>
  );

  // Instead of returning just the loading screen, we'll render the app with an overlay
  const showLoadingOverlay = isLoading;

  if (error) {
    return (
      <div className="p-4">
        <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-red-800 font-semibold mb-2">Error</h2>
          <p className="text-red-600">{error}</p>
          <div className="mt-4 space-x-4">
            <button
              onClick={() => window.location.reload()}
              className="text-red-600 hover:text-red-800"
            >
              Retry
            </button>
            <button
              onClick={handleFullAppTransition}
              className="text-red-600 hover:text-red-800"
            >
              Switch to full app
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen flex flex-col ${
        step === STEPS.WELCOME ? "bg-gray-50" : ""
      }`}
    >
      {/* Always show the navbar with logo */}
      <Navbar />

      {/* Show progress indicator based on flow configuration */}
      {shouldShowProgress(step) && (
        <ProgressIndicator
          currentStep={step}
          flowType={flowType} // Pass the flow type
        />
      )}

      <div className="flex-1">
        <div className="max-w-md mx-auto">{currentStepComponent}</div>
        {/* Show loading overlay when loading */}
        {showLoadingOverlay && <LoadingOverlay />}
      </div>
    </div>
  );
};
export default App;
